export default {
    props: {
        config: { type: Object, required: true },
        data: { type: Object, required: true }
    },
    data() {
        return {
            selectedLocationId: this.config.prefilterLocation || 0,
            selectedDepartmentId: this.config.prefilterDepartment || 0
        };
    },
    computed: {
        locations() { return this.data.locations || []; },
        departments() { return this.data.departments || []; },
        employees() { return this.data.employees || []; },
        selectedLocation() {
            return this.locations.find(l => l.id === this.selectedLocationId) || null;
        },
        // Flatten every employee into one entry per role.
        roles() {
            const list = [];
            this.employees.forEach(emp => {
                (emp.roles || []).forEach(role => {
                    list.push({
                        employeeId: emp.id,
                        name: emp.name,
                        sortOrder: emp.sortOrder,
                        title: role.title,
                        employeeTitle: emp.title,
                        email: emp.email,
                        phone: emp.phone,
                        linkedIn: emp.linkedIn,
                        imageUrl: emp.imageUrl,
                        locationId: role.locationId,
                        departmentId: role.departmentId
                    });
                });
            });
            return list;
        },
        visibleRoles() {
            const hasLoc = this.selectedLocationId > 0;
            const hasDep = this.selectedDepartmentId > 0;
            return this.roles.filter(r => {
                if (hasLoc && r.locationId !== this.selectedLocationId) return false;
                if (hasDep && r.departmentId !== this.selectedDepartmentId) return false;
                // Default landing: no location and no department -> sales departments only.
                if (!hasLoc && !hasDep) {
                    const dep = this.departmentById(r.departmentId);
                    if (!dep || !dep.isDefault) return false;
                }
                return true;
            });
        },
        groups() {
            const hasLoc = this.selectedLocationId > 0;
            const byDep = {};
            this.visibleRoles.forEach(r => {
                (byDep[r.departmentId] = byDep[r.departmentId] || []).push(r);
            });
            return this.departments
                .filter(d => byDep[d.id])
                .slice()
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map(d => {
                    let roles = byDep[d.id];
                    if (!hasLoc) {
                        roles = this.dedupeByEmployee(roles);
                    }
                    roles = roles.slice().sort((a, b) => a.sortOrder - b.sortOrder);
                    return { department: d, roles };
                });
        }
    },
    methods: {
        departmentById(id) {
            return this.departments.find(d => d.id === id) || null;
        },
        // When no location is selected, collapse an employee's multiple roles in the
        // same department to one card. If the role titles differ, fall back to the
        // employee's base title.
        dedupeByEmployee(roles) {
            const map = new Map();
            roles.forEach(r => {
                const existing = map.get(r.employeeId);
                if (!existing) {
                    map.set(r.employeeId, Object.assign({}, r));
                } else if (existing.title !== r.title) {
                    existing.title = existing.employeeTitle;
                }
            });
            return Array.from(map.values());
        },
        lines(text) {
            return (text || '').split(/\r?\n/).filter(l => l.trim().length);
        }
    },
    template: `
<div class="locations-overview__inner">
    <div v-if="!config.hideFilters" class="locations-overview__filters">
        <span class="locations-overview__filters-label">Hvem vil du kontakte?</span>
        <div class="locations-overview__filter">
            <select v-model.number="selectedLocationId" class="locations-overview__select">
                <option :value="0">Alle lokationer</option>
                <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
        </div>
        <div class="locations-overview__filter">
            <select v-model.number="selectedDepartmentId" class="locations-overview__select">
                <option :value="0">Afdeling</option>
                <option v-for="dep in departments" :key="dep.id" :value="dep.id">{{ dep.name }}</option>
            </select>
        </div>
    </div>

    <section v-if="selectedLocation" class="locations-overview__location">
        <h2 class="locations-overview__location-name">{{ selectedLocation.name }}</h2>
        <div class="locations-overview__location-contact">
            <a v-if="selectedLocation.hasPhone" :href="'tel:' + selectedLocation.phone" class="locations-overview__contact-item locations-overview__contact-item--phone">{{ selectedLocation.phone }}</a>
            <a v-if="selectedLocation.hasEmail" :href="'mailto:' + selectedLocation.email" class="locations-overview__contact-item locations-overview__contact-item--email">{{ selectedLocation.email }}</a>
        </div>
        <div class="locations-overview__location-details">
            <div v-if="selectedLocation.hasAddress" class="locations-overview__address">
                <h3 class="locations-overview__label">Adresse</h3>
                <p class="locations-overview__address-lines"><template v-for="(line, i) in lines(selectedLocation.address)" :key="i">{{ line }}<br></template></p>
                <a :href="selectedLocation.mapUrl" target="_blank" rel="noopener" class="locations-overview__map-link">Se på kort</a>
            </div>
            <div class="locations-overview__hours">
                <div v-if="selectedLocation.hasMoreInfo" class="locations-overview__more-info" v-html="selectedLocation.moreInfo"></div>
                <h3 class="locations-overview__label">Åbningstider</h3>
                <div class="locations-overview__hours-grid">
                    <div v-if="selectedLocation.hasAdministrationOpeningHours" class="locations-overview__hours-block">
                        <p class="locations-overview__hours-title">Administration:</p>
                        <p class="locations-overview__hours-value"><template v-for="(line, i) in lines(selectedLocation.administrationOpeningHours)" :key="i">{{ line }}<br></template></p>
                    </div>
                    <div v-if="selectedLocation.hasWorkshopOpeningHours" class="locations-overview__hours-block">
                        <p class="locations-overview__hours-title">Værkstedet:</p>
                        <p class="locations-overview__hours-value"><template v-for="(line, i) in lines(selectedLocation.workshopOpeningHours)" :key="i">{{ line }}<br></template></p>
                    </div>
                </div>
                <p v-if="selectedLocation.hasCvr" class="locations-overview__cvr">CVR: {{ selectedLocation.cvr }}</p>
            </div>
        </div>
    </section>

    <hr v-if="selectedLocation && groups.length" class="locations-overview__divider">

    <div v-if="groups.length" class="locations-overview__groups">
        <section v-for="group in groups" :key="group.department.id" class="locations-overview__department">
            <h2 class="locations-overview__department-name" :class="{ 'locations-overview__department-name--secondary': !group.department.isDefault }">{{ group.department.name }}</h2>
            <ul class="locations-overview__list">
                <li v-for="role in group.roles" :key="role.employeeId + '-' + group.department.id" class="locations-overview__card">
                    <div v-if="role.imageUrl" class="locations-overview__card-image">
                        <img :src="role.imageUrl" :alt="role.name" loading="lazy">
                    </div>
                    <div class="locations-overview__card-body">
                        <h3 class="locations-overview__card-name">{{ role.name }}</h3>
                        <p v-if="role.title" class="locations-overview__card-title">{{ role.title }}</p>
                        <a v-if="role.email" :href="'mailto:' + role.email" class="locations-overview__card-email">{{ role.email }}</a>
                        <a v-if="role.phone" :href="'tel:' + role.phone" class="locations-overview__card-phone">{{ role.phone }}</a>
                        <a v-if="role.linkedIn" :href="role.linkedIn" target="_blank" rel="noopener" class="locations-overview__card-linkedin" aria-label="LinkedIn"></a>
                    </div>
                </li>
            </ul>
        </section>
    </div>
    <p v-else class="locations-overview__empty">Ingen medarbejdere fundet.</p>
</div>
`
};
